import { useState, useEffect, useCallback } from 'react';
import { getTasks, createTask, updateTaskStatus, deleteTask } from '../services/api';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { format, isToday, isTomorrow, isPast, addDays } from 'date-fns';
import { de } from 'date-fns/locale';
import { Plus, CheckCircle2, Circle, Trash2, Calendar, Flag, X, Clock, Zap, Target, Star, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import Sidebar from '../components/layout/Sidebar';

const TasksPage = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', due_date: '', priority: 'medium' });

  const loadTasks = useCallback(async () => {
    try {
      const response = await getTasks(filter !== 'all' ? { status: filter } : {});
      setTasks(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleCreateTask = async () => {
    if (!newTask.title) {
      toast.error('Bitte einen Titel eingeben');
      return;
    }

    try {
      await createTask(newTask);
      toast.success('Aufgabe erstellt');
      setNewTaskOpen(false);
      setNewTask({ title: '', due_date: '', priority: 'medium' });
      loadTasks();
    } catch (error) {
      toast.error('Fehler beim Erstellen');
    }
  };

  const handleToggleTask = async (task) => {
    const newStatus = task.status === 'done' ? 'open' : 'done';
    setTasks(tasks.map(t => t.id === task.id ? { ...t, status: newStatus } : t));

    try {
      await updateTaskStatus(task.id, newStatus);
    } catch (error) {
      setTasks(tasks.map(t => t.id === task.id ? { ...t, status: task.status } : t));
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await deleteTask(taskId);
      setTasks(tasks.filter(t => t.id !== taskId));
      toast.success('Aufgabe gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const getPriorityConfig = (priority) => {
    switch (priority) {
      case 'high': return { color: 'red', icon: Zap, label: 'Dringend', borderClass: 'border-red-500', bgClass: 'bg-red-500/20', textClass: 'text-red-400' };
      case 'medium': return { color: 'amber', icon: Flag, label: 'Normal', borderClass: 'border-amber-500', bgClass: 'bg-amber-500/20', textClass: 'text-amber-400' };
      default: return { color: 'blue', icon: Target, label: 'Niedrig', borderClass: 'border-blue-500', bgClass: 'bg-blue-500/20', textClass: 'text-blue-400' };
    }
  };

  const getDateLabel = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (isToday(date)) return { text: 'Heute', color: 'text-red-400', urgent: true };
    if (isTomorrow(date)) return { text: 'Morgen', color: 'text-amber-400', urgent: false };
    if (isPast(date)) return { text: 'Überfällig', color: 'text-red-500', urgent: true };
    return { text: format(date, 'dd. MMM', { locale: de }), color: 'text-gray-400', urgent: false };
  };

  // Group tasks
  const openTasks = tasks.filter(t => t.status !== 'done');
  const doneTasks = tasks.filter(t => t.status === 'done');
  const urgentTasks = openTasks.filter(t => t.priority === 'high' || (t.due_date && (isToday(new Date(t.due_date)) || isPast(new Date(t.due_date)))));

  // Stats
  const completedToday = doneTasks.filter(t => t.completed_at && isToday(new Date(t.completed_at))).length;
  const totalOpen = openTasks.length;
  const totalUrgent = urgentTasks.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen relative overflow-hidden ${document.documentElement.classList.contains('dark') ? 'galaxy-bg' : 'bg-background'}`} data-testid="tasks-page">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none dark:hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-violet-500/30 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-500/30 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
      </div>

      <div className="flex relative z-10">
        <Sidebar />

        <main className="flex-1 min-h-screen">
          {/* Header */}
          <header className="glass-panel sticky top-0 z-40 border-b border-white/10 dark:bg-white/5 dark:border-white/10">
            <div className="px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/30">
                  <CheckCircle2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Aufgaben
                  </h1>
                  <p className="text-xs text-muted-foreground">{totalOpen} offen · {totalUrgent} dringend</p>
                </div>
              </div>

              <Dialog open={newTaskOpen} onOpenChange={setNewTaskOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white rounded-xl gap-2 shadow-lg shadow-violet-500/25">
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">Neue Aufgabe</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="glass-panel border-white/20 dark:bg-gray-900/95 dark:border-white/10">
                  <DialogHeader>
                    <DialogTitle className="text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Neue Aufgabe erstellen</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label className="text-foreground">Titel *</Label>
                      <Input
                        value={newTask.title}
                        onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                        placeholder="Was muss erledigt werden?"
                        className="mt-2 glass-input dark:bg-white/10 dark:border-white/20 text-foreground"
                        data-testid="new-task-title-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-foreground">Fällig am</Label>
                        <Input
                          type="date"
                          value={newTask.due_date}
                          onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                          className="mt-2 glass-input dark:bg-white/10 dark:border-white/20 text-foreground"
                        />
                      </div>
                      <div>
                        <Label className="text-foreground">Priorität</Label>
                        <Select value={newTask.priority} onValueChange={(v) => setNewTask({ ...newTask, priority: v })}>
                          <SelectTrigger className="mt-2 glass-input dark:bg-white/10 dark:border-white/20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="glass-panel dark:bg-gray-900 dark:border-white/10">
                            <SelectItem value="high" className="text-foreground">🔴 Dringend</SelectItem>
                            <SelectItem value="medium" className="text-foreground">🟡 Normal</SelectItem>
                            <SelectItem value="low" className="text-foreground">🔵 Niedrig</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button onClick={handleCreateTask} className="w-full bg-gradient-to-r from-violet-600 to-purple-600 rounded-xl shadow-lg shadow-violet-500/25" data-testid="new-task-submit-btn">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Aufgabe erstellen
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </header>

          {/* Main Content */}
          <div className="p-6">
            <div className="max-w-4xl mx-auto">
              {/* Stats Cards */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                <motion.div 
                  className="glass-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
                      <Target className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-foreground">{totalOpen}</p>
                      <p className="text-sm text-muted-foreground">Offen</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div 
                  className="glass-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center shadow-lg shadow-red-500/30">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-red-500">{totalUrgent}</p>
                      <p className="text-sm text-muted-foreground">Dringend</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div 
                  className="glass-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                      <Star className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-emerald-500">{doneTasks.length}</p>
                      <p className="text-sm text-muted-foreground">Erledigt</p>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Filter Tabs */}
              <div className="flex gap-2 mb-6">
                {[
                  { value: 'all', label: 'Alle' },
                  { value: 'open', label: 'Offen' },
                  { value: 'done', label: 'Erledigt' },
                ].map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => setFilter(tab.value)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      filter === tab.value
                        ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg shadow-violet-500/30'
                        : 'bg-white/10 text-muted-foreground hover:text-foreground hover:bg-white/20'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Task List */}
              <div className="space-y-3">
                <AnimatePresence>
                  {tasks.length === 0 ? (
                    <motion.div 
                      className="glass-card rounded-2xl p-12 text-center dark:bg-white/5"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
                      <p className="text-lg font-medium text-foreground mb-2">Keine Aufgaben</p>
                      <p className="text-muted-foreground mb-6">Erstellen Sie Ihre erste Aufgabe</p>
                      <Button onClick={() => setNewTaskOpen(true)} className="bg-gradient-to-r from-violet-600 to-purple-600 rounded-xl">
                        <Plus className="w-4 h-4 mr-2" />
                        Neue Aufgabe
                      </Button>
                    </motion.div>
                  ) : (
                    tasks.map((task, idx) => {
                      const priority = getPriorityConfig(task.priority);
                      const dateLabel = getDateLabel(task.due_date);
                      const PriorityIcon = priority.icon;

                      return (
                        <motion.div
                          key={task.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ delay: idx * 0.05 }}
                          className={`bg-white/5 border border-white/10 rounded-2xl p-4 group hover:bg-white/10 transition-all ${
                            task.status === 'done' ? 'opacity-60' : ''
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            {/* Checkbox */}
                            <button
                              onClick={() => handleToggleTask(task)}
                              className="flex-shrink-0"
                            >
                              {task.status === 'done' ? (
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
                                  <CheckCircle2 className="w-5 h-5 text-white" />
                                </div>
                              ) : (
                                <div className={`w-8 h-8 rounded-full border-2 ${priority.borderClass} hover:bg-white/10 transition-colors flex items-center justify-center`}>
                                  <Circle className={`w-5 h-5 ${priority.textClass}`} />
                                </div>
                              )}
                            </button>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium text-white ${task.status === 'done' ? 'line-through text-gray-500' : ''}`}>
                                {task.title}
                              </p>
                              <div className="flex items-center gap-3 mt-1">
                                {/* Priority Badge */}
                                <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${priority.bgClass} ${priority.textClass}`}>
                                  <PriorityIcon className="w-3 h-3" />
                                  {priority.label}
                                </span>
                                
                                {/* Date Badge */}
                                {dateLabel && (
                                  <span className={`flex items-center gap-1 text-xs ${dateLabel.color}`}>
                                    <Clock className="w-3 h-3" />
                                    {dateLabel.text}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Delete Button */}
                            <button
                              onClick={() => handleDeleteTask(task.id)}
                              className="opacity-0 group-hover:opacity-100 p-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-all"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </motion.div>
                      );
                    })
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default TasksPage;
